import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  App,
  Avatar,
  Button,
  Flex,
  Form,
  Input,
  Checkbox,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd';
import { Link } from 'react-router';
import {
  CampoDeTelefone,
  EscolhaDeTema,
  iniciaisDoNome,
  MENSAGEM_DE_FALHA_INESPERADA,
  mensagemDeErro,
  type FalhaDeAutenticacao,
} from '@titularis/ui';
import { FOCUS_MODULES, focosValidos, type FocusModule } from '@titularis/api-client';
import { authClient, useSession } from '../../auth';
import { registrarTela } from '../../analytics';
import { LinhaDeAjuste, SecaoDeAjustes, TituloDaPagina } from '../../components/Ajustes';
import { moduloEmPreparo } from '../../grandesModulos';
import { nomeDoProduto } from '../../produtos';

/**
 * Conta: o perfil, a senha e a aparência.
 *
 * O e-mail aparece e não se edita. Ele é a identidade da conta e a chave de
 * recuperação; trocá-lo exige verificar o endereço novo antes de valer, e esse
 * fluxo não existe no servidor (o `changeEmail` do BetterAuth está desligado).
 * Um campo que aceita o valor e não o aplica é pior que campo nenhum - então
 * ele é só leitura, dizendo o caminho para quem precisar mudar.
 */
interface CamposDaSenha {
  atual: string;
  nova: string;
  confirmacao: string;
}

/**
 * Um campo do perfil: mostra o que o servidor tem, guarda o que se digita e
 * grava sob demanda.
 *
 * **Duas travessuras moram aqui, e é por isso que nome e celular usam o mesmo
 * gancho em vez de cada um repetir o truque.**
 *
 * A primeira: o rascunho nasce `null`, e não como cópia do valor da sessão. A
 * sessão chega depois do primeiro render, e copiá-la num efeito faria o campo
 * nascer vazio e piscar - além de ser exatamente o `setState` dentro de efeito
 * que o lint proíbe. Nulo, o campo mostra o valor do servidor até alguém
 * encostar nele.
 *
 * A segunda é uma corrida vista no navegador em 02/09/2026: gravei o nome, o
 * servidor respondeu 200 e passou a devolvê-lo em `get-session`, e mesmo assim
 * o campo e a barra lateral voltaram ao nome antigo, com o aviso de "Nome
 * atualizado" na tela. Quem olha entende que a gravação falhou. A causa é o
 * `refetch` do BetterAuth, que resolve sem garantir que o store da sessão já
 * tenha o valor novo. **Não dá para consertar a corrida daqui - dá para deixar
 * de depender dela**: o que o servidor confirmou vale sobre o store, e é o que
 * `confirmado` guarda. Quando o store alcança, os dois concordam e ele deixa
 * de ter efeito.
 */
function useCampoDoPerfil({
  gravado,
  gravar,
  sucesso,
  falha,
  aoGravar,
}: {
  /** O valor que a sessão traz do servidor. */
  gravado: string | null | undefined;
  gravar: (valor: string) => Promise<{ error?: FalhaDeAutenticacao | null }>;
  sucesso: string;
  /** Mensagem quando o servidor recusa. */
  falha: string;
  /** Corre depois de uma gravação aceita - recarregar a sessão, por exemplo. */
  aoGravar?: () => void;
}) {
  const { message } = App.useApp();
  const [rascunho, setRascunho] = useState<string | null>(null);
  const [confirmado, setConfirmado] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const salvo = confirmado ?? gravado ?? '';
  const valor = rascunho ?? salvo;

  const salvar = async (normalizado: string) => {
    setSalvando(true);
    try {
      const { error } = await gravar(normalizado);
      if (error) {
        message.error(mensagemDeErro(error, falha));
        return;
      }
      /*
       * A ordem importa. Primeiro fixamos o que o servidor confirmou, e só
       * então largamos o rascunho: se largássemos antes, o campo cairia no
       * valor do store por um instante - que é justamente o valor velho.
       */
      setConfirmado(normalizado);
      setRascunho(null);
      message.success(sucesso);
      aoGravar?.();
    } catch {
      message.error(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setSalvando(false);
    }
  };

  return { valor, salvo, editar: setRascunho, salvando, salvar };
}

export function ContaPage() {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const { data: sessao, refetch } = useSession();

  const [trocandoSenha, setTrocandoSenha] = useState(false);
  const [formDaSenha] = Form.useForm<CamposDaSenha>();

  useEffect(() => registrarTela('/configuracoes/conta'), []);

  /*
   * As etapas em foco. Gravam direto pelo `updateUser`, como os demais campos
   * do perfil, e o `refetch` da sessão é o que faz a Home reagir sem recarregar.
   */
  const foco = focosValidos(sessao?.user.focusModules);
  const [salvandoFoco, setSalvandoFoco] = useState(false);

  const salvarFoco = async (escolhas: FocusModule[]) => {
    setSalvandoFoco(true);
    try {
      const { error } = await authClient.updateUser({ focusModules: escolhas });
      if (error) throw new Error(error.message);
      await refetch();
    } catch {
      message.error(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setSalvandoFoco(false);
    }
  };

  const campoDoNome = useCampoDoPerfil({
    gravado: sessao?.user.name,
    gravar: (name) => authClient.updateUser({ name }),
    sucesso: 'Nome atualizado.',
    falha: 'Não conseguimos salvar o nome.',
    // A barra lateral lê a sessão: ela precisa ser recarregada para acompanhar.
    // Sem `await`: a tela já está certa, e nada aqui depende do que o store vai
    // responder.
    aoGravar: () => void refetch(),
  });

  const nome = campoDoNome.valor;
  const nomeMudou = nome.trim().length > 0 && nome.trim() !== campoDoNome.salvo;

  const campoDoTelefone = useCampoDoPerfil({
    gravado: sessao?.user.phone,
    /*
     * Campo vazio grava **nulo**, não string vazia. Quem nunca informou o
     * celular tem nulo na coluna; se apagar gravasse `''`, "sem celular"
     * passaria a ter duas formas, e um `where phone is not null` contaria como
     * telefone quem não tem. Medido contra o servidor: o BetterAuth aceita o
     * nulo e devolve nulo.
     */
    gravar: (phone) => authClient.updateUser({ phone: phone === '' ? null : phone }),
    sucesso: 'Celular atualizado.',
    falha: 'Não conseguimos salvar o celular.',
  });

  /**
   * O celular já forma um número completo do país escolhido.
   *
   * Começa `true` porque o campo nasce com o que está gravado, que é válido por
   * construção - e um campo que nasce marcado de vermelho acusa quem ainda não
   * digitou nada.
   */
  const [telefoneValido, setTelefoneValido] = useState(true);
  /**
   * Já dá para criticar o número: alguém digitou e saiu do campo.
   *
   * Sem isto, o aviso de número incompleto aparece no primeiro dígito e fica
   * piscando em vermelho durante a digitação inteira - e todo número é
   * incompleto até o último dígito.
   */
  const [telefoneCriticavel, setTelefoneCriticavel] = useState(false);
  const telefone = campoDoTelefone.valor;
  const telefoneMudou = telefone !== campoDoTelefone.salvo;
  // Apagar o número é uma edição legítima: quem não quer mais ser chamado no
  // celular precisa poder tirá-lo, e campo vazio não tem o que validar.
  const telefoneSalvavel = telefoneMudou && (telefone === '' || telefoneValido);
  const criticarTelefone = telefoneCriticavel && telefone !== '' && !telefoneValido;

  /*
   * As contas de entrada da pessoa: a de senha (`credential`) e as sociais.
   * Vem do BetterAuth, que não aparece no `openapi.json` e por isso não passa
   * pelo `api-client` - mas continua sendo dado de servidor, então continua
   * atrás do TanStack Query, com a mesma chave para as duas linhas que a leem.
   */
  const contas = useQuery({
    queryKey: ['contas-conectadas'],
    queryFn: async () => (await authClient.listAccounts()).data ?? [],
  });

  const google = contas.data?.find((conta) => conta.providerId === 'google');
  /*
   * Desvincular a única forma de entrar tranca a pessoa para fora. O BetterAuth
   * recusa por conta própria, mas um botão que só falha depois do clique é pior
   * do que um botão desligado com o motivo escrito ao lado.
   */
  const soTemGoogle = (contas.data?.length ?? 0) < 2;
  const [mexendoNoGoogle, setMexendoNoGoogle] = useState(false);

  /*
   * A foto **se mostra, e não se edita**. Ela vem do Google quando a conta é
   * vinculada - é o `account.create.after` da API que copia o `picture` do
   * `id_token` -, e a tela só a exibe.
   *
   * Havia aqui um "Usar minhas iniciais", que gravava `image: null`. Saiu em
   * 06/09/2026: era o único controle da linha, e um botão para apagar o que a
   * pessoa não pôs ali não é ajuste de perfil.
   */
  const foto = sessao?.user.image ?? undefined;

  const conectarGoogle = async () => {
    setMexendoNoGoogle(true);
    try {
      /*
       * Volta para esta mesma tela: quem clicou aqui quer ver o resultado aqui,
       * e não ser despejado na Home depois de passar pelo Google.
       */
      const { error } = await authClient.linkSocial({
        provider: 'google',
        callbackURL: window.location.href,
      });
      if (error) {
        message.error(mensagemDeErro(error, 'Não foi possível conectar o Google.'));
        setMexendoNoGoogle(false);
      }
      // No sucesso o navegador sai para o Google: não desligamos o estado, pela
      // mesma razão do botão da tela de entrada.
    } catch {
      message.error(MENSAGEM_DE_FALHA_INESPERADA);
      setMexendoNoGoogle(false);
    }
  };

  const desconectarGoogle = async () => {
    setMexendoNoGoogle(true);
    try {
      const { error } = await authClient.unlinkAccount({ providerId: 'google' });
      if (error) {
        message.error(mensagemDeErro(error, 'Não conseguimos desconectar o Google.'));
        return;
      }
      message.success('Google desconectado.');
      void contas.refetch();
    } catch {
      message.error(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setMexendoNoGoogle(false);
    }
  };

  const trocarSenha = async ({ atual, nova }: CamposDaSenha) => {
    setTrocandoSenha(true);
    try {
      const { error } = await authClient.changePassword({
        currentPassword: atual,
        newPassword: nova,
        /*
         * Derruba as outras sessões. Quem troca a senha em geral troca porque
         * desconfia de alguém, e deixar a sessão do outro aparelho de pé
         * esvaziaria o gesto.
         */
        revokeOtherSessions: true,
      });

      if (error) {
        message.error(mensagemDeErro(error, 'A senha atual não confere.'));
        return;
      }

      formDaSenha.resetFields();
      message.success('Senha alterada. As outras sessões foram encerradas.');
    } catch {
      message.error(MENSAGEM_DE_FALHA_INESPERADA);
    } finally {
      setTrocandoSenha(false);
    }
  };

  return (
    <Space orientation="vertical" size="large" style={{ width: '100%' }}>
      <TituloDaPagina
        titulo="Conta"
        descricao="Seus dados, sua senha e como a plataforma se apresenta para você."
      />

      <SecaoDeAjustes titulo="Meu perfil" descricao="Como você aparece na plataforma.">
        {/*
          A foto vem primeiro porque é o que a pessoa vê de si na barra lateral,
          e é o único dado do perfil que ela não digitou - dizer de onde ele veio
          é metade da linha.
        */}
        {/*
          Três estados, e o do meio é o que a cópia ingênua erra: **Google
          conectado e sem foto**. É onde estão todas as contas vinculadas antes
          de 04/09/2026, quando a foto passou a ser copiada - e mandar "conecte
          o Google" para quem já conectou é a pior instrução possível.
        */}
        <LinhaDeAjuste
          primeira
          titulo="Foto"
          descricao={
            foto
              ? 'Veio da sua conta do Google, quando você a conectou.'
              : google
                ? 'Sem foto. Reconecte o Google abaixo para trazer a de lá.'
                : 'Sem foto: usamos suas iniciais. Conecte o Google para usar a de lá.'
          }
        >
          <Avatar size={43} src={foto} alt={sessao?.user.name}>
            {iniciaisDoNome(sessao?.user.name, sessao?.user.email)}
          </Avatar>
        </LinhaDeAjuste>

        <LinhaDeAjuste titulo="Nome" descricao="Como chamamos você por aqui.">
          <Flex gap={8} wrap>
            <Input
              value={nome}
              onChange={(evento) => campoDoNome.editar(evento.target.value)}
              onPressEnter={() => nomeMudou && void campoDoNome.salvar(nome.trim())}
              style={{ width: 220 }}
              maxLength={80}
              aria-label="Nome"
            />
            <Button
              type="primary"
              disabled={!nomeMudou}
              loading={campoDoNome.salvando}
              onClick={() => void campoDoNome.salvar(nome.trim())}
            >
              Salvar
            </Button>
          </Flex>
        </LinhaDeAjuste>

        <LinhaDeAjuste
          titulo="E-mail"
          descricao="É por ele que você entra. Para trocar, fale com a gente."
        >
          <Typography.Text type="secondary">{sessao?.user.email}</Typography.Text>
        </LinhaDeAjuste>

        {/*
          **O celular vazio aparece como pendente, e não como campo em branco.**
          Ele é opcional por decisão (D-8: obrigar produz número inventado, que
          é pior que campo vazio porque parece preenchido), mas quem pulou o
          passo das boas-vindas não tinha nenhum lembrete - e em 17/09/2026 dois
          de onze cadastros tinham telefone. A etiqueta é o lembrete: diz o que
          falta sem trancar nada.
        */}
        <LinhaDeAjuste
          titulo={
            <Flex align="center" gap={8} wrap>
              Celular
              {!campoDoTelefone.salvo ? <Tag color="warning">a preencher</Tag> : null}
            </Flex>
          }
          descricao="Opcional. É por onde a gente fala com você se algo travar numa compra ou no seu acesso."
        >
          <Flex gap={8} wrap align="flex-start">
            <Flex vertical gap={4}>
              <CampoDeTelefone
                value={telefone}
                onChange={(numero, valido) => {
                  campoDoTelefone.editar(numero ?? '');
                  setTelefoneValido(valido);
                }}
                onBlur={() => setTelefoneCriticavel(true)}
                status={criticarTelefone ? 'error' : undefined}
                disabled={campoDoTelefone.salvando}
                aria-label="Celular"
              />
              {criticarTelefone ? (
                <Typography.Text type="danger" style={{ fontSize: token.fontSizeSM }}>
                  Número incompleto para o país escolhido.
                </Typography.Text>
              ) : null}
            </Flex>
            <Button
              type="primary"
              disabled={!telefoneSalvavel}
              loading={campoDoTelefone.salvando}
              onClick={() => void campoDoTelefone.salvar(telefone)}
            >
              Salvar
            </Button>
          </Flex>
        </LinhaDeAjuste>

        {/*
          A etapa que as boas-vindas perguntam.

          **Ela existe aqui porque o passo 2 promete que existe** ("você muda
          quando quiser, em Configurações") - e até 05/09/2026 a promessa não era
          cumprida. Virou obrigatória quando as boas-vindas passaram a parar
          sozinhas na primeira questão respondida: sem este controle, quem
          dispensou três vezes e foi estudar perderia a escolha para sempre.

          Salva no clique, sem botão de confirmar: é preferência de navegação,
          não dado de cadastro - e desfazer é escolher outra.
        */}
        <LinhaDeAjuste
          titulo="Etapas que você estuda"
          descricao="Pode marcar mais de uma. É o que nos diz o que comentar primeiro."
        >
          {/*
            `Checkbox.Group`, e não o `Segmented` de antes: aquele é de escolha
            única por desenho, e a pergunta passou a aceitar várias. Salva no
            clique, sem botão de confirmar - é declaração de interesse, não dado
            de cadastro, e desfazer é desmarcar.
          */}
          <Checkbox.Group
            value={foco}
            disabled={salvandoFoco}
            onChange={(valores) => void salvarFoco(valores as FocusModule[])}
            options={FOCUS_MODULES.map((chave) => ({
              value: chave,
              label: moduloEmPreparo(chave)
                ? `${nomeDoProduto(chave)} (sendo comentada)`
                : nomeDoProduto(chave),
            }))}
          />
        </LinhaDeAjuste>
      </SecaoDeAjustes>

      <SecaoDeAjustes
        titulo="Contas conectadas"
        descricao="Por onde você pode entrar, além do e-mail e senha."
      >
        {/*
          A etiqueta acompanha o **nome**, e não o botão.

          Ela dizia o mesmo de antes, mas do lado direito, e ali ficava
          alinhada com nada: o controle daquele lado é uma pilha de duas
          alturas - botão em cima, aviso embaixo -, e o `align="center"`
          pendurava a etiqueta no vão entre os dois, a 240px do botão e a 50px
          da descrição. Sobrava no meio da linha.

          Do lado esquerdo ela volta ao lugar de sempre: "Google" é o que a
          linha trata, "Conectada" é o estado desse assunto, e o que se faz
          continua sozinho à direita.
        */}
        <LinhaDeAjuste
          primeira
          titulo={
            <Flex component="span" align="center" gap={8} wrap>
              Google
              {google ? <Tag color="success">Conectada</Tag> : null}
            </Flex>
          }
          descricao={
            google
              ? 'Você pode entrar com um toque, sem digitar a senha.'
              : 'Conecte para entrar com um toque e trazer a foto do seu perfil.'
          }
        >
          {/*
            O invólucro existe para o bloco não esticar no celular: lá a linha
            empilha e a coluna do controle ocupa a largura toda, e sem ele o
            `align="flex-end"` de dentro jogaria o botão contra a borda direita
            - sozinho entre os controles da tela, que são todos à esquerda.
          */}
          <Flex wrap>
            {google ? (
              <Flex vertical gap={4} align="flex-end">
                <Button
                  danger
                  disabled={soTemGoogle}
                  loading={mexendoNoGoogle}
                  onClick={() => void desconectarGoogle()}
                >
                  Desconectar
                </Button>
                {/*
                  Quem entrou pelo Google e nunca criou senha não tem outra
                  porta. Dizer isso ao lado do botão desligado evita o clique
                  que só falharia - e aponta a saída, que é criar uma senha.
                */}
                {soTemGoogle ? (
                  <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    É sua única forma de entrar. Crie uma senha antes.
                  </Typography.Text>
                ) : null}
              </Flex>
            ) : (
              <Button loading={mexendoNoGoogle} onClick={() => void conectarGoogle()}>
                Conectar
              </Button>
            )}
          </Flex>
        </LinhaDeAjuste>
      </SecaoDeAjustes>

      <SecaoDeAjustes
        titulo="Senha"
        descricao="Trocar a senha encerra as sessões abertas nos outros aparelhos."
      >
        <Form<CamposDaSenha>
          form={formDaSenha}
          layout="vertical"
          requiredMark={false}
          onFinish={(campos) => void trocarSenha(campos)}
          disabled={trocandoSenha}
          style={{ paddingTop: 4 }}
        >
          <Form.Item
            name="atual"
            label="Senha atual"
            rules={[{ required: true, message: 'Digite a senha atual.' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          <Form.Item
            name="nova"
            label="Nova senha"
            rules={[
              { required: true, message: 'Escolha uma senha.' },
              { min: 8, message: 'Use pelo menos 8 caracteres.' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirmacao"
            label="Repita a nova senha"
            dependencies={['nova']}
            rules={[
              { required: true, message: 'Repita a senha.' },
              ({ getFieldValue }) => ({
                validator: (_rule, valor: string) =>
                  !valor || getFieldValue('nova') === valor
                    ? Promise.resolve()
                    : Promise.reject(new Error('As senhas não coincidem.')),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Flex justify="flex-end" gap={8} wrap>
            {/* Quem não lembra a senha atual não consegue trocá-la aqui - e é
                por isso que a saída fica do lado, e não escondida no rodapé. */}
            <Link to="/esqueci-a-senha">
              <Button type="text">Esqueci minha senha</Button>
            </Link>
            <Button type="primary" htmlType="submit" loading={trocandoSenha}>
              Alterar senha
            </Button>
          </Flex>
        </Form>
      </SecaoDeAjustes>

      {/*
        Aqui a seção **não** usa `LinhaDeAjuste`: os três desenhos não cabem na
        coluna da direita de uma linha de rótulo, e o que eles mostram já diz o
        que a linha diria. É a forma da tela de conta da Voa - título, uma
        frase, e as opções ocupando a largura toda.
      */}
      <SecaoDeAjustes
        titulo="Aparência"
        descricao="Vale neste navegador. Sistema acompanha a preferência do seu aparelho."
      >
        <EscolhaDeTema />
      </SecaoDeAjustes>
    </Space>
  );
}
